import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { UserType } from '../../types/user/user.type';
import { history } from '../../utils/utilities';
import {
  setIncludeSuborgs,
  setSelectedCompany,
  setSelectedLocation,
  setSelectedUserAccess,
} from '../shared/shared.slice';
import { setUser, setOrganizationSwitchLoading, setOrganizationSwitchError } from './auth.slice';
import NetworkService from '../../utils/network.service';
import { parseApiError, rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import { LoginResponse } from '../../types/auth/login-response';
import { setOktaLoginSource } from '../okta/okta.config';

export const getCurrentUser = createAsyncThunk<
  { user: UserType; init: boolean },
  { init: boolean },
  {
    state: RootReducerState;
  }
>('auth/getCurrentUser', async (params, thunkAPI) => {
  try {
    const response = await axios.get<UserType>(`${process.env.REACT_APP_API_URL}/users/profile`);
    return { user: response.data, init: params.init };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching user profile!' });
  }
});

export const postLogin = createAsyncThunk<
  LoginResponse,
  { password: string; username: string },
  { state: RootReducerState }
>('auth/postLogin', async (data, thunkAPI) => {
  try {
    const response = await axios.post<LoginResponse>(`${process.env.REACT_APP_API_URL}/auth/login`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during login!' });
  }
});

const postLogout = createAsyncThunk<{ success: true }, { userId: number }, { state: RootReducerState }>(
  'auth/postLogout/no-loader',
  async (data, thunkAPI) => {
    try {
      await axios.post<{
        user: UserType;
        access_token: string;
      }>(`${process.env.REACT_APP_API_URL}/auth/logout`, data);
      return { success: true };
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during logout!' });
    }
  }
);

export const postForgotPassword = createAsyncThunk<
  { success: boolean },
  { email: string; id: number },
  { state: RootReducerState }
>('auth/postForgotPassword', async (data, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/users/forgotPassword/`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during forgot password!' });
  }
});

export const postResetPassword = createAsyncThunk<
  { success: boolean },
  { password: string; token: string },
  { state: RootReducerState }
>('auth/postResetPassword', async (data, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/users/restorePassword`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during reset password!' });
  }
});

export const logout = createAsyncThunk<void, void, { state: RootReducerState }>('auth/logout', async (_, thunkAPI) => {
  try {
    history.push('/authentication/login');
    localStorage.removeItem('token');
    setOktaLoginSource(null);

    const user = thunkAPI.getState().authReducer.user;
    if (user) await thunkAPI.dispatch(postLogout({ userId: user.id }));
    thunkAPI.dispatch(setUser(null));
    thunkAPI.dispatch(setSelectedCompany(null));
    thunkAPI.dispatch(setSelectedLocation(null));
    thunkAPI.dispatch(setIncludeSuborgs(false));
    thunkAPI.dispatch(setSelectedUserAccess(null));

    delete axios.defaults.headers.common['Authorization'];
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during logout!' });
  }
});

export const switchOrganization = createAsyncThunk<
  { user: UserType; access_token: string },
  { organizationId: number; userAccess: unknown },
  { state: RootReducerState }
>('auth/switchOrganization', async ({ organizationId, userAccess }, thunkAPI) => {
  const state = thunkAPI.getState();

  if (state.authReducer.organizationSwitch.isLoading) {
    return thunkAPI.rejectWithValue('Organization switch already in progress');
  }

  try {
    thunkAPI.dispatch(setOrganizationSwitchLoading(true));

    const response = await axios.post<{
      access_token: string;
    }>(`${process.env.REACT_APP_API_URL}/user-access/switchToOrganization`, {
      organization: organizationId,
    });

    const newToken = response.data.access_token;
    localStorage.setItem('token', newToken);
    NetworkService.setupDefaultHeaders(newToken);
    const userResponse = await thunkAPI.dispatch(getCurrentUser({ init: false })).unwrap();
    thunkAPI.dispatch(setSelectedUserAccess(userAccess));

    return {
      user: userResponse.user,
      access_token: newToken,
    };
  } catch (error) {
    const errorMessage = parseApiError({ error, fallback: 'Failed to switch organization' });
    thunkAPI.dispatch(setOrganizationSwitchError(errorMessage));
    return thunkAPI.rejectWithValue(errorMessage);
  }
});
